import { useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { useUserIdentitiesQuery, type UserIdentity } from '@/features/cms/hooks/useUserIdentitiesQuery';

/**
 * QA-398 — 참여자 목록에 회원을 "이름(로그인 ID)"로 보이고, 누르면 회원 정보 모달.
 * 일괄 신원 조회(POST /api/admin/v1/users/identities — 이름·이메일 마스킹, 로그인 ID 는 user #39)로 한 번에 가져온다.
 */
export const usePledgeMembers = (userIds: ReadonlyArray<string>): ReadonlyMap<string, UserIdentity> =>
  useUserIdentitiesQuery(userIds).data ?? new Map<string, UserIdentity>();

export const memberLabel = (m: Pick<UserIdentity, 'maskedName' | 'loginId'> | undefined): string | null => {
  if (!m) return null;
  const name = m.maskedName?.trim() || '';
  const login = m.loginId?.trim() || '';
  if (name && login) return `${name}(${login})`;
  return name || login || null;
};

const Row = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <>
    <dt className="text-xs text-[color:var(--content-muted)]">{label}</dt>
    <dd className="text-sm text-[color:var(--content-strong)]">{value && value.trim() !== '' ? value : '—'}</dd>
  </>
);

export const PledgeMemberButton = ({ userId, member }: { userId: string; member: UserIdentity | undefined }) => {
  const { t } = useTranslation('funding');
  const [open, setOpen] = useState(false);
  const label = memberLabel(member) ?? t('funding.pledges.memberUnknown', '회원 정보 없음');
  return (
    <>
      <button
        type="button"
        className="text-left text-sm font-medium underline-offset-2 hover:underline"
        style={{ color: 'var(--content-strong)' }}
        onClick={() => setOpen(true)}
        data-testid="pledge-member"
      >
        {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[440px]" data-testid="pledge-member-modal">
          <DialogHeader>
            <DialogTitle>{t('funding.pledges.memberTitle', '회원 정보')}</DialogTitle>
            <DialogDescription>{label}</DialogDescription>
          </DialogHeader>
          {member ? (
            <dl className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-1.5 [overflow-wrap:anywhere]">
              <Row label={t('funding.pledges.memberName', '이름')} value={member.maskedName} />
              <Row label={t('funding.pledges.memberLoginId', '로그인 ID')} value={member.loginId} />
              <Row label={t('funding.pledges.memberEmail', '이메일')} value={member.maskedEmail} />
            </dl>
          ) : (
            <p className="text-sm text-[color:var(--content-muted)]">{t('funding.pledges.memberUnknown', '회원 정보 없음')}</p>
          )}
          <DialogFooter>
            <Button asChild variant="outline" size="sm">
              <Link to={`/users/members/${userId}`} data-testid="pledge-member-detail-link">
                {t('funding.pledges.memberOpen', '회원 상세 보기')}
              </Link>
            </Button>
            <Button type="button" size="sm" onClick={() => setOpen(false)}>
              {t('funding.shippingDetail.closeAction', '닫기')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
